import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { PetWeightHistoryModel } from "../shared-models/pet-profile.model";
import { petWeightUnits, WeightUnitType } from "../../../../petszel-owner/src/app/service/pet-id.service";

dayjs.extend(utc);

@Injectable({
  providedIn: 'root'
})
export class SharedPetUtilsService {

  ageRangeInMonthsCat: { [key: string]: [number, number] } = {
    'Super Senior': [180, 500],
    Senior: [132, 179],
    Mature: [84, 131],
    Adult: [13, 83],
    Junior: [5, 12],
    Kitten: [0, 4]
  };

  ageRangeInMonthsDog: { [key: string]: [number, number] } = {
    'Super Senior': [145, 500],
    Senior: [96, 144],
    Mature: [60, 95],
    Adult: [13, 59],
    Junior: [5, 12],
    Puppy: [0, 4]
  };

  ageRanges: { [key: string]: { [key: string]: [number, number] } } = {
    Cat: this.ageRangeInMonthsCat,
    Dog: this.ageRangeInMonthsDog,
  };

  constructor() { }

  latestWeight(weightHistory: PetWeightHistoryModel[]): PetWeightHistoryModel | null {
    if (!weightHistory || weightHistory.length === 0) return null;

    return weightHistory[weightHistory.length - 1];
  }

  calculateAge(dob: string): string {
    if (!dob) return '';
    if (dob === '0001-01-01T00:00:00') return '';

    const dobUTC = dayjs.utc(dob);
    const years = dayjs.utc().diff(dobUTC, 'y');
    if (years > 0) return years.toString() + ' years old';

    const months = dayjs.utc().diff(dobUTC, 'month');
    if (months > 0) return months.toString() + ' months old';

    const days = dayjs.utc().diff(dobUTC, 'day', true);
    return days.toString() + ' days old';
  }

  sexAlteredLabel(
    sex: string | null | undefined,
    altered: string | null | undefined,
  ): string {
    if (!sex) return '';
    if (!altered) return '';

    if (sex === 'Male') {
      if (altered === 'Yes') return 'Neutered';
      if (altered === 'No') return 'Intact';
      return 'Unknown';
    }

    if (sex === 'Female') {
      if (altered === 'Yes') return 'Spayed';
      if (altered === 'No') return 'Intact';
      return 'Unknown';
    }

    return '';
  }

  calculatePetAgeInMonths(petDateOfBirth: string): number {
    return dayjs.utc().diff(petDateOfBirth, 'month');
  }

  calculatePetLifeStage(petDateOfBirth: string, species: string): string {
    const speciesAgeRanges = this.ageRanges[species];
    const months = this.calculatePetAgeInMonths(petDateOfBirth);

    const lifeStage =  Object.keys(speciesAgeRanges).find(range =>
      months >= speciesAgeRanges[range][0] && months <= speciesAgeRanges[range][1]);

    return lifeStage || 'N/A';
  }

  petLatestWeightToDisplay(weightHistory: PetWeightHistoryModel[], ownerCountry: string = 'USA'): string {
    if (weightHistory && weightHistory.length > 0) {
      const lastWeight = weightHistory[weightHistory.length - 1];

      if (lastWeight.weightLbs && lastWeight.weightLbs > 0) {

        const unitTo = ownerCountry.trim().toLowerCase() === 'canada' || ownerCountry.trim().toLowerCase() === 'ca'? petWeightUnits.kg
          : petWeightUnits.lbs;

        return this.getPetWeightConverted(lastWeight.weightLbs, petWeightUnits.lbs, unitTo);
      } else {
        return '';
      }
    } else {
      return '';
    }
  }

  private getPetWeightConverted(weight: number, unitFrom: WeightUnitType, unitTo: WeightUnitType): string {
    const kgToLb = 2.2046226218;
    const lbToKg = 0.45359237;

    if (unitFrom === petWeightUnits.kg && unitTo === petWeightUnits.kg) {
      return `${weight} Kg`;
    }

    if (unitFrom === petWeightUnits.lbs && unitTo === petWeightUnits.kg) {
      const kilograms = weight * lbToKg;
      return `${Number(kilograms.toFixed(2))} Kg`;
    }

    const totalPounds = unitFrom === petWeightUnits.kg ? weight * kgToLb : weight;
    let pounds = Math.floor(totalPounds);
    let ounces = Math.round((totalPounds - pounds) * 16);

    if (ounces === 16) {
      pounds += 1;
      ounces = 0;
    }

    return ounces === 0 ? `${pounds} Lbs` : `${pounds} Lbs ${ounces} Oz`;
  }
}
